import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Attesa, Avviso } from '../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Modale } from '../componenti/base.tsx'

/**
 * Amministrazione > Utenti: «Disattiva» e «Riattiva» (464, richiesta della Direzione del 17/10/2026).
 *
 * Disattivare toglie l'accesso subito e lascia lo storico; la finestra dice prima che cosa si chiude
 * (incarichi, sessioni, inviti) e avvisa se la persona e' un mediatore con mediazioni in programma.
 * Riattivare prepara un invito nuovo: gli incarichi non tornano da soli, e la finestra lo dice.
 */

type IncaricoInEssere = { id: string; ruolo: string; ruolo_nome: string; ambito_tipo: string; ambito_nome: string | null }
type Mediatore = { id: string; nome: string; cognome: string; attivo: boolean; mediazioni_in_programma: number }
type Anteprima = {
  id: string; nome: string; cognome: string; email: string; stato: string
  incarichi: IncaricoInEssere[]; sessioni_aperte: number; inviti_pendenti: number
  mediatore: Mediatore | null; impedimento: string | null
}
type EsitoDisattivazione = {
  incarichi_revocati: number; sessioni_chiuse: number; inviti_annullati: number
  mediatore: (Mediatore & { disattivato: boolean }) | null
}
export type Precedente = { ruolo: string; ruolo_nome: string; ambito_tipo: string; ambito_nome: string | null }
export type EsitoRiattivazione = {
  id: string; nome: string; cognome: string
  codice_da_consegnare_a_voce: string; collegamento: string; scade_il: string
  email_inviata: boolean; motivo_email: string | null
  incarichi_precedenti: Precedente[]
  mediatore: Mediatore | null
}

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

const quante = (n: number, una: string, tante: string) => (n === 1 ? `1 ${una}` : `${n} ${tante}`)

/** «Operatore interno · Sede di Perugia», per gli elenchi degli incarichi. */
export function descriviIncarico(i: { ruolo_nome: string; ambito_tipo: string; ambito_nome: string | null }): string {
  if (i.ambito_tipo === 'organizzazione') return `${i.ruolo_nome} · tutta CIDIS`
  return i.ambito_nome ? `${i.ruolo_nome} · ${i.ambito_nome}` : i.ruolo_nome
}

export function DisattivaUtente({ id, chiudi, fatto }: {
  id: string
  chiudi: () => void
  fatto: (testo: string) => void
}) {
  const [anteprima, setAnteprima] = useState<Anteprima | null>(null)
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  useEffect(() => {
    api.get<Anteprima>(`/amministrazione/utenti/${id}/disattivazione`).then(setAnteprima).catch((e) => setErrore(messaggio(e)))
  }, [id])

  async function disattiva() {
    if (!anteprima) return
    setErrore(null); setInCorso(true)
    try {
      const e = await api.post<EsitoDisattivazione>(`/amministrazione/utenti/${id}/disattiva`, { motivo: motivo.trim() })
      const parti = [
        `${anteprima.nome} ${anteprima.cognome} è disattivato e non può più entrare.`,
        e.incarichi_revocati ? `Incarichi revocati: ${e.incarichi_revocati}.` : '',
        e.mediatore?.disattivato ? 'È stato tolto anche dai mediatori attivi dell’albo.' : '',
        e.mediatore && !e.mediatore.disattivato && e.mediatore.mediazioni_in_programma
          ? `Ha ancora ${quante(e.mediatore.mediazioni_in_programma, 'mediazione', 'mediazioni')} in programma: `
            + 'affidale a un altro mediatore o annullale.'
          : '',
      ]
      fatto(parti.filter(Boolean).join(' '))
    } catch (err) {
      setErrore(messaggio(err))
    } finally {
      setInCorso(false)
    }
  }

  const titolo = anteprima ? `Disattiva ${anteprima.nome} ${anteprima.cognome}` : 'Disattiva'
  const manca = motivo.trim().length < 5
  return (
    <Modale titolo={titolo} larga chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
              {anteprima && !anteprima.impedimento && (
                <Bottone variante="critico" inCorso={inCorso} disabled={manca} onClick={() => void disattiva()}>
                  Disattiva
                </Bottone>
              )}
            </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!anteprima ? (!errore && <Attesa testo="Caricamento…" />) : anteprima.impedimento
        ? <Avviso tipo="attenzione">{anteprima.impedimento}</Avviso>
        : (
          <>
            <p className="nota-modulo">{anteprima.email}</p>
            <h3 className="operatori-titoletto operatori-sezione">Che cosa succede</h3>
            <ul className="operatori-conseguenze">
              <li>
                Non potrà più entrare: {anteprima.sessioni_aperte
                  ? `${quante(anteprima.sessioni_aperte, 'sessione aperta si chiude', 'sessioni aperte si chiudono')} subito, `
                  : ''}
                la password non vale più{anteprima.inviti_pendenti ? ' e l’invito non ancora usato si annulla' : ''}.
              </li>
              <li>
                {anteprima.incarichi.length === 0
                  ? 'Non ha incarichi in essere.'
                  : <>Si revocano gli incarichi in essere:{' '}
                    {anteprima.incarichi.map(descriviIncarico).join('; ')}.</>}
              </li>
              <li>
                Resta tutto quello che ha registrato: schede, interventi ed eventi continuano a riportare
                il suo nome.
              </li>
              <li>Si può riattivare in seguito, con un invito nuovo; gli incarichi andranno assegnati di nuovo.</li>
            </ul>

            {anteprima.mediatore && (anteprima.mediatore.mediazioni_in_programma > 0
              ? (
                <Avviso tipo="attenzione">
                  È il mediatore «{anteprima.mediatore.nome} {anteprima.mediatore.cognome}» dell’albo, con{' '}
                  {quante(anteprima.mediatore.mediazioni_in_programma, 'mediazione', 'mediazioni')} in programma.
                  Affidale a un altro mediatore o annullale dalla scheda della persona: finché restano, il
                  mediatore resta attivo nell’albo, ma le email delle mediazioni non gli arrivano più.
                  Poi disattivalo dall’albo dei mediatori.
                </Avviso>
              )
              : anteprima.mediatore.attivo && (
                <Avviso tipo="informazione">
                  È anche il mediatore «{anteprima.mediatore.nome} {anteprima.mediatore.cognome}» dell’albo:
                  esce dai mediatori attivi, così non riceve più mediazioni. Si riattiva dall’albo.
                </Avviso>
              ))}

            <AreaTesto etichetta="Motivo della disattivazione" obbligatorio righe={3} maxLength={500}
                       valore={motivo} cambia={setMotivo}
                       aiuto="Per esempio: fine della collaborazione. Resta nella sua scheda e nel registro degli eventi." />
            {manca && <p className="nota-modulo">Per disattivare scrivi il motivo (almeno cinque caratteri).</p>}
          </>
        )}
    </Modale>
  )
}

export function RiattivaUtente({ utente, chiudi, riattivato }: {
  utente: { id: string; nome: string; cognome: string }
  chiudi: () => void
  riattivato: (esito: EsitoRiattivazione) => void
}) {
  const [motivo, setMotivo] = useState('')
  const [errore, setErrore] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const motivoCorto = motivo.trim().length > 0 && motivo.trim().length < 5

  async function riattiva() {
    setErrore(null); setInCorso(true)
    try {
      riattivato(await api.post<EsitoRiattivazione>(`/amministrazione/utenti/${utente.id}/riattiva`,
        { motivo: motivo.trim() || null }))
    } catch (e) {
      setErrore(messaggio(e))
    } finally {
      setInCorso(false)
    }
  }

  return (
    <Modale titolo={`Riattiva ${utente.nome} ${utente.cognome} · passo 1 di 3`} chiudi={chiudi}
            azioni={<>
              <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
              <Bottone variante="primario" inCorso={inCorso} disabled={motivoCorto} onClick={() => void riattiva()}>
                Riattiva e prepara l’invito
              </Bottone>
            </>}>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <ul className="operatori-conseguenze">
        <li>
          Si prepara un invito nuovo, come per una persona appena aggiunta: il collegamento e il codice
          da dettare a voce. La password di prima non vale più.
        </li>
        <li>Finché non accetta l’invito, l’utenza resta «invitata».</li>
        <li>
          Gli incarichi revocati con la disattivazione non tornano da soli: nel passo successivo vedi
          quelli che aveva e scegli quali assegnare di nuovo.
        </li>
      </ul>
      <AreaTesto etichetta="Motivo della riattivazione" righe={2} maxLength={500} valore={motivo} cambia={setMotivo}
                 errore={motivoCorto ? 'Scrivi almeno cinque caratteri, oppure lascia vuoto.' : null}
                 aiuto="Facoltativo. Resta nella sua scheda." />
    </Modale>
  )
}
